import { ParceiroDashboard } from "@/components/dashboard/ParceiroDashboard";
import { MetricsGrid } from "@/components/dashboard/MetricsGrid";
import { CRMFunnelChart } from "@/components/dashboard/CRMFunnelChart";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { usePermissions } from "@/components/auth";
import React, { Suspense, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { financeiroService } from "@/services/financeiroService";
import { ContratosService } from "@/services/contratosService";
import { PropostasService } from "@/services/propostasService";
import { ParceirosService } from "@/services/parceirosService";
import { VendedoresService } from "@/services/vendedoresService";
import { supabase } from "@/integrations/supabase/client";
import { isProdutoLeadExcluidoFunilSolarDashboard } from "@/constants/leadsProduto";
import {
  TrendingUp,
  Calendar,
  Clock,
  Target,
  Users,
  Sun,
  DollarSign,
  Package,
  UserPlus,
  Ruler,
  ArrowLeftRight,
  Calculator
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

function QuickActions() {
  const permissions = usePermissions();
  const navigate = useNavigate();
  
  // Verificar se as permissões estão carregadas
  if (!permissions || !permissions.proposals || !permissions.contracts || !permissions.leads) {
    return (
      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="text-lg">Ações Rápidas</CardTitle>
          <CardDescription>Carregando permissões...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-4">
            <div className="w-6 h-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const actions = [
    {
      title: "Novo Lead",
      description: "Adicionar cliente",
      icon: Users,
      color: "bg-info",
      show: permissions.leads.canCreate(),
      onClick: () => navigate("/clientes")
    },
    {
      title: "Novo Parceiro",
      description: "Cadastrar parceiro",
      icon: UserPlus,
      color: "bg-primary",
      show: permissions.parceiros?.canCreate?.() || false,
      onClick: () => navigate("/parceiros")
    },
    {
      title: "Novo Dimensionamento",
      description: "Criar dimensionamento",
      icon: Ruler,
      color: "bg-accent",
      show: permissions.proposals.canCreate() || permissions.leads.canRead(),
      onClick: () => navigate("/dimensionamento")
    },
    {
      title: "Nova Transação",
      description: "Adicionar transação",
      icon: ArrowLeftRight,
      color: "bg-success",
      show: (permissions.contracts.canRead() || permissions.proposals.canRead()) && 
            permissions.userRole !== 'vendedor' && 
            permissions.userRole !== 'parceiro',
      onClick: () => navigate("/financeiro")
    },
    {
      title: "Calculadoras",
      description: "Simulador solar e parcelamento",
      icon: Calculator,
      color: "bg-warning",
      show: permissions.userRole === 'vendedor',
      onClick: () => navigate("/calculadora")
    },
  ].filter(action => action.show);

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="text-lg">Ações Rápidas</CardTitle>
        <CardDescription>Principais funcionalidades</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          {actions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Button
                key={index}
                variant="ghost"
                className="h-auto p-4 flex flex-col items-center gap-2 border border-border hover:border-primary/50"
                onClick={action.onClick}
              >
                <div className={`p-3 rounded-lg ${action.color} text-white`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-sm">{action.title}</p>
                </div>
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const permissions = usePermissions();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalPlacas: 0,
    potenciaInstalada: 0,
    contratosFechados: 0,
    receitaTotal: 0,
    lucroLiquido: 0,
    novosLeads: 0,
    taxaConversao: 0,
    lucroPorPlaca: 0,
    leadsCapturados: 0,
    totalParceiros: 0,
    totalVendedores: 0,
    totalPropostas: 0,
    valorTotalPropostas: 0
  });
  const [funnelData, setFunnelData] = useState<any[]>([]);
  const [financialData, setFinancialData] = useState<any[]>([]);

  useEffect(() => {
    async function loadDashboardData() {
      // Evitar chamadas se já estiver carregando dados ou se as permissões ainda não estiverem prontas
      if (!permissions?.userProfile?.id) return;
      
      try {
        setLoading(true);
        console.log('📊 Dashboard: Carregando dados reais...');
        
        // 1. Fetch Leads for Funnel and Conversion
        const leadsResponse = await fetchWithAuth('/api/leads?crm_funil=vendas');
        const allLeads = await leadsResponse.json();
        
        // Filtrar apenas leads que não são de carregador/wallbox para o funil solar
        const leads = allLeads.filter(
          (l: any) =>
            !isProdutoLeadExcluidoFunilSolarDashboard(l.produto) && l.crmFunil !== 'parceiro_prospeccao'
        );
        
        const stagesCount: Record<string, number> = {
          'lead-capturado': 0,
          'qualificacao': 0,
          'analise-necessidades': 0,
          'proposta-valor': 0,
          'visita-tecnica': 0,
          'negocio-fechado': 0,
          'follow-up': 0,
          'leads-perdidos': 0
        };
        
        let recentLeads = 0;
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        leads.forEach((l: any) => {
          if (l.etapa && stagesCount[l.etapa] !== undefined) {
            stagesCount[l.etapa]++;
          }
          if (new Date(l.created_at) > thirtyDaysAgo) {
            recentLeads++;
          }
        });

        const funnel = [
          { name: 'Capturado', value: stagesCount['lead-capturado'], color: '#3b82f6' },
          { name: 'Qualificação', value: stagesCount['qualificacao'], color: '#60a5fa' },
          { name: 'Análise', value: stagesCount['analise-necessidades'], color: '#93c5fd' },
          { name: 'Proposta', value: stagesCount['proposta-valor'], color: '#bfdbfe' },
          { name: 'Visita Técnica', value: stagesCount['visita-tecnica'], color: '#1e40af' },
          { name: 'Fechado', value: stagesCount['negocio-fechado'], color: '#10b981' }
        ];
        setFunnelData(funnel);

        // 2. Fetch Contracts for Solar Metrics
        const contratos = await ContratosService.listarContratos();
        const closedContracts = contratos.filter(c => c.status === 'finalizado' || c.status === 'em_andamento');
        
        let totalPlacas = 0;
        let potenciaTotal = 0;
        let receitaTotal = 0;
        
        closedContracts.forEach(c => {
          if (c.proposta) {
            totalPlacas += c.proposta.total_paineis || 0;
            potenciaTotal += c.proposta.potencia_total_paineis || 0;
          }
          receitaTotal += c.valor_total || 0;
        });

        // 3. Fetch Financial Data
        const resumoFin = await financeiroService.obterResumoFinanceiro();
        const dadosGrafico = await financeiroService.obterDadosGrafico('mes');
        setFinancialData(dadosGrafico);

        // 4. Calculate Profit per Panel based on contracts profit/loss
        // Buscar todas as despesas e impostos dos contratos fechados
        const todasDespesas = await financeiroService.listarContasPagar();
        const todosCustos = await financeiroService.listarCustosObras();
        const todosImpostos = todosCustos.filter(c => c.categoria === 'Impostos' && c.contrato_id);
        
        // Calcular lucro total dos contratos fechados
        let lucroTotalContratos = 0;
        
        for (const contrato of closedContracts) {
          // Calcular receitas do contrato (valor total do contrato)
          const receitasContrato = contrato.valor_total || 0;
          
          // Buscar despesas do contrato
          const despesasContrato = todasDespesas
            .filter(d => d.contrato_id === contrato.id)
            .reduce((acc, d) => acc + (d.valor || 0), 0);
          
          // Buscar impostos do contrato
          const impostosContrato = todosImpostos
            .filter(i => i.contrato_id === contrato.id)
            .reduce((acc, i) => acc + i.valor, 0);
          
          // Calcular lucro/prejuízo do contrato
          const lucroContrato = receitasContrato - despesasContrato - impostosContrato;
          lucroTotalContratos += lucroContrato;
        }
        
        // Calcular lucro por placa: lucro total dos contratos dividido pelo total de placas
        const profitPerPanel = totalPlacas > 0 ? (lucroTotalContratos / totalPlacas) : 0;

        // 5. Calculate Conversion Rate
        const conversionRate = leads.length > 0 ? (closedContracts.length / leads.length) * 100 : 0;
        
        // 6. Total de leads capturados
        const totalLeadsCapturados = leads.length;

        // 7. Total de parceiros cadastrados
        const parceiros = await ParceirosService.getParceiros();
        const totalParceiros = parceiros.length;

        // 8. Total de vendedores cadastrados (apenas admins podem visualizar)
        let totalVendedores = 0;
        if (permissions.userRole === 'admin') {
          try {
            const vendedores = await VendedoresService.getVendedores();
            totalVendedores = vendedores.length;
          } catch (error) {
            console.warn("Não foi possível buscar vendedores:", error);
          }
        }

        // 9. Total de propostas geradas (únicas por lead) e valor total
        let totalPropostasUnicas = 0;
        let valorTotalPropostas = 0;
        try {
          const propostas = await PropostasService.carregarPropostas();
          const leadsComProposta = new Set(propostas.map(p => p.lead_id));
          totalPropostasUnicas = leadsComProposta.size;
          
          // Buscar valores das propostas (valor_total_kits + preco_integracao)
          // Para cada lead único, buscar a proposta mais recente e somar seu valor
          const leadsIds = Array.from(leadsComProposta);
          for (const leadId of leadsIds) {
            try {
              const { data: propostaData, error: propostaError } = await supabase
                .from('propostas_clientes')
                .select('valor_total_kits, preco_integracao')
                .eq('lead_id', leadId)
                .eq('dimensionamento_finalizado', true)
                .order('created_at', { ascending: false })
                .limit(1)
                .single();
              
              if (!propostaError && propostaData) {
                const valorKits = (propostaData.valor_total_kits as number) || 0;
                const valorIntegracao = (propostaData.preco_integracao as number) || 0;
                valorTotalPropostas += valorKits + valorIntegracao;
              }
            } catch (error) {
              console.warn(`Erro ao buscar valor da proposta para lead ${leadId}:`, error);
            }
          }
        } catch (error) {
          console.warn("Não foi possível buscar propostas:", error);
        }

        setMetrics({
          totalPlacas,
          potenciaInstalada: potenciaTotal,
          contratosFechados: closedContracts.length,
          receitaTotal,
          lucroLiquido: resumoFin.saldo_total,
          novosLeads: recentLeads,
          taxaConversao: conversionRate,
          lucroPorPlaca: profitPerPanel,
          leadsCapturados: totalLeadsCapturados,
          totalParceiros,
          totalVendedores,
          totalPropostas: totalPropostasUnicas,
          valorTotalPropostas
        });

      } catch (error) {
        console.error("Erro ao carregar dados do dashboard:", error);
      } finally {
        setLoading(false);
      }
    }

    loadDashboardData();
  }, [permissions?.userProfile?.id]); // Depender apenas do ID do usuário para estabilidade

  if (!permissions) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando permissões...</p>
        </div>
      </div>
    );
  }

  // Se for parceiro, renderizar o dashboard específico
  if (permissions.userRole === 'parceiro') {
    return (
      <div className="space-y-6 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Dashboard do Parceiro</h1>
            <p className="text-muted-foreground">Acompanhamento dos seus resultados e comissões</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted p-2 rounded-md">
            <Clock className="h-4 w-4" />
            <span>Última atualização: {new Date().toLocaleTimeString('pt-BR')}</span>
          </div>
        </div>
        <ParceiroDashboard />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard Operacional</h1>
          <p className="text-muted-foreground">Acompanhamento em tempo real da Sun7 Solar</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted p-2 rounded-md">
          <Clock className="h-4 w-4" />
          <span>Última atualização: {new Date().toLocaleTimeString('pt-BR')}</span>
        </div>
      </div>

      {/* Metrics Grid */}
      {permissions.canViewMetrics && permissions.canViewMetrics() && (
        <MetricsGrid 
          metrics={metrics} 
          exclude={(permissions.userRole === 'vendedor' || permissions.userRole === 'parceiro') ? ["Vendedores Cadastrados"] : []}
        />
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Charts Section */}
        <div className="lg:col-span-2 space-y-6">
          {permissions.userRole !== 'vendedor' && permissions.userRole !== 'parceiro' && (
            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle className="text-lg">Fluxo Financeiro</CardTitle>
                <CardDescription>Receitas vs Despesas (últimos meses)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={financialData}>
                      <defs>
                        <linearGradient id="colorReceitas" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="mes" axisLine={false} tickLine={false} />
                      <YAxis axisLine={false} tickLine={false} tickFormatter={(v) => `R$ ${v/1000}k`} />
                      <Tooltip />
                      <Area type="monotone" dataKey="receitas" stroke="#10b981" fillOpacity={1} fill="url(#colorReceitas)" />
                      <Area type="monotone" dataKey="despesas" stroke="#f43f5e" fill="transparent" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          <CRMFunnelChart data={funnelData} />
        </div>

        {/* Side widgets */}
        <div className="space-y-6">
          <QuickActions />
          <Card className="shadow-soft">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Meta de Expansão
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Potência Instalada</span>
                  <span className="font-bold">{metrics.potenciaInstalada.toFixed(1)} / 5000 kWp</span>
                </div>
                <Progress value={(metrics.potenciaInstalada / 5000) * 100} className="h-2" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Contratos Fechados</span>
                  <span className="font-bold">{metrics.contratosFechados} / 50</span>
                </div>
                <Progress value={(metrics.contratosFechados / 50) * 100} className="h-2" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>




    </div>
  );
}
